import { AxiosError } from 'axios';

export type ErrorData = {
	errorCode: string;
	errorMessage: string;
};

interface AxiosErrorResponseData extends Error {
	response: {
		data: object;
	};
}

export const apiResponseErrorCodes = [
	'ENTITY_NOT_FOUND',
	'BAD_USER_INPUT',
	'INVALID_MG',
	'INVALID_PO',
	'INVALID_FILE',
	'SERVER_UNAVAILABLE',
	'SERVER_ERROR',
	'INVALID_TASK_STATUS'
] as const;

export const apiResponseErrorByCode: Record<(typeof apiResponseErrorCodes)[number], string> = {
	ENTITY_NOT_FOUND: 'Entity not found',
	BAD_USER_INPUT: 'Bad user input',
	INVALID_MG: 'Invalid measurement group',
	INVALID_PO: 'Invalid process object',
	INVALID_FILE: 'Invalid file',
	SERVER_UNAVAILABLE: 'Server unavailable',
	SERVER_ERROR: 'Server error',
	INVALID_TASK_STATUS: 'Task is not in progress and cannot be revoked'
};

export const apiResponseErrorByStatus = {
	401: 'Unauthenticated',
	404: apiResponseErrorByCode.ENTITY_NOT_FOUND,
	413: 'File is too large.',
	400: apiResponseErrorByCode.BAD_USER_INPUT,
	500: apiResponseErrorByCode.SERVER_ERROR,
	503: apiResponseErrorByCode.SERVER_UNAVAILABLE
};

const DEFAULT_ERROR_DATA = {
	errorCode: 'UNKNOWN',
	errorMessage: 'Sorry, an unexpected error has occurred.'
};

function isAxiosResponseErrorData(error: unknown): error is AxiosErrorResponseData {
	return (
		!!error &&
		typeof error === 'object' &&
		'name' in error &&
		error.name === 'AxiosError' &&
		'response' in error &&
		typeof error.response === 'object' &&
		!!error.response &&
		'data' in error.response &&
		typeof error.response.data === 'object'
	);
}

export function extractErrorData(error: unknown): ErrorData {
	if (isAxiosResponseErrorData(error)) {
		if ('errorMessage' in error.response.data && 'errorCode' in error.response.data) {
			return {
				errorMessage: error.response.data.errorMessage + '',
				errorCode: error.response.data.errorCode + ''
			};
		}

		return DEFAULT_ERROR_DATA;
	}

	if (error instanceof AxiosError) {
		const status = error.status as keyof typeof apiResponseErrorByStatus;

		return {
			errorCode: 'code' in error ? error.status + '' : 'UNKNOWN',
			errorMessage: status && apiResponseErrorByStatus[status] ? apiResponseErrorByStatus[status] : error.message
		};
	}

	if (error instanceof Error) {
		return {
			errorCode: 'code' in error ? error.code + '' : 'UNKNOWN',
			errorMessage: error.message
		};
	}

	if (typeof error === 'string') {
		return {
			errorCode: 'UNKNOWN',
			errorMessage: error
		};
	}

	return DEFAULT_ERROR_DATA;
}

export function getUIErrorMessage(error: Error): string {
	const errorCode = extractErrorData(error).errorCode as (typeof apiResponseErrorCodes)[number];

	if (apiResponseErrorCodes.includes(errorCode)) {
		return apiResponseErrorByCode[errorCode];
	}

	const status = (error as AxiosError).status as keyof typeof apiResponseErrorByStatus;
	return apiResponseErrorByStatus[status] || 'An unknown error occurred.';
}
